import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Calendar, XCircle, Megaphone, Loader2, Users, RefreshCw } from "lucide-react";
import api from "@/lib/api";

const EVENT_ICON = {
  broadcast: Megaphone,
  offering_created: Sparkles,
  offering_updated: Sparkles,
  offering_cancelled: XCircle,
  blackout_added: Calendar,
};
const EVENT_ACCENT = {
  broadcast: "#CCFF00",
  offering_created: "#CCFF00",
  offering_updated: "#A3A3A3",
  offering_cancelled: "#FF4500",
  blackout_added: "#FF4500",
};

const RELATIVE = (iso) => {
  if (!iso) return "";
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
};

/**
 * Feed — Instagram-style chronological stream of updates from every coach
 * the student follows. Groups by coach for readability without breaking the
 * newest-first order (we still render one flat list, but each card is
 * clearly attributed).
 */
export default function Feed() {
  const [items, setItems] = useState([]);
  const [coachesFollowed, setCoachesFollowed] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/feed?limit=60");
      setItems(data.items || []);
      setCoachesFollowed(data.coaches_followed || 0);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    // Track which coach's events we've already shown a divider for.
    const seen = new Set();
    return items.map((it) => ({
      ...it,
      first_for_coach: !seen.has(it.coach_id) ? (seen.add(it.coach_id), true) : false,
    }));
  }, [items]);

  return (
    <div className="space-y-6" data-testid="feed-page">
      <header className="flex items-end justify-between fade-up flex-wrap gap-3">
        <div>
          <div className="label-mono mb-2">FOLLOWING</div>
          <h1 className="h-display text-4xl sm:text-5xl tracking-tighter">Your feed</h1>
          <p className="text-sm text-[#A3A3A3] mt-2 max-w-xl leading-relaxed">
            Updates from every coach you follow — new offerings, calendar changes, blackouts,
            and their own announcements. Newest first.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-xs text-[#A3A3A3] inline-flex items-center gap-2" data-testid="feed-coaches-count">
            <Users className="w-3.5 h-3.5" />
            {coachesFollowed} coach{coachesFollowed === 1 ? "" : "es"}
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="text-[10px] uppercase tracking-wider text-[#A3A3A3] hover:text-[#CCFF00] inline-flex items-center gap-1 disabled:opacity-40"
            data-testid="feed-refresh"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </header>

      {loading && !items.length ? (
        <div className="card-surface p-8 text-center text-[#A3A3A3] flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <EmptyFeed followedCount={coachesFollowed} />
      ) : (
        <div className="space-y-3" data-testid="feed-items">
          {grouped.map((it) => <FeedItem key={it.id} it={it} />)}
        </div>
      )}
    </div>
  );
}

function EmptyFeed({ followedCount }) {
  return (
    <div className="card-surface p-8 fade-up" data-testid="feed-empty">
      <div className="label-mono mb-2" style={{ color: "#CCFF00" }}>NOTHING HERE YET</div>
      {followedCount === 0 ? (
        <>
          <h3 className="h-display text-2xl tracking-tight mb-2">Follow a coach to get started.</h3>
          <p className="text-sm text-[#A3A3A3] leading-relaxed mb-4">
            Tap the <span className="text-[#CCFF00] font-semibold">Follow</span> button on any coach&apos;s
            profile to see their new offerings and updates here.
          </p>
          <Link to="/find-coach" className="btn-volt px-4 py-2 text-xs inline-flex" data-testid="feed-empty-cta">
            Find coaches
          </Link>
        </>
      ) : (
        <>
          <h3 className="h-display text-2xl tracking-tight mb-2">All caught up.</h3>
          <p className="text-sm text-[#A3A3A3] leading-relaxed">
            You&apos;re following {followedCount} coach{followedCount === 1 ? "" : "es"} — when they publish something new, it&apos;ll appear here.
          </p>
        </>
      )}
    </div>
  );
}

function FeedItem({ it }) {
  const Icon = EVENT_ICON[it.event_type] || Sparkles;
  const accent = EVENT_ACCENT[it.event_type] || "#CCFF00";
  const inner = (
    <div className="card-surface p-4 sm:p-5 hover:border-[#CCFF00] transition-colors" data-testid={`feed-item-${it.id}`}>
      <div className="flex items-start gap-3">
        <div
          className="w-9 h-9 flex items-center justify-center shrink-0"
          style={{ background: accent, color: "#0A0A0A" }}
        >
          <Icon className="w-4 h-4" strokeWidth={2.5} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-white">{it.coach_name || "Coach"}</span>
            <span className="text-[10px] uppercase tracking-wider text-[#525252]">·</span>
            <span className="text-[10px] uppercase tracking-wider" style={{ color: accent }}>
              {(it.event_type || "").replace(/_/g, " ")}
            </span>
            <span className="text-[10px] uppercase tracking-wider text-[#525252] ml-auto">
              {RELATIVE(it.created_at)}
            </span>
          </div>
          <div className="text-sm text-white mt-1 leading-snug">{it.title}</div>
          {it.body && <div className="text-xs text-[#A3A3A3] mt-1 leading-relaxed line-clamp-3">{it.body}</div>}
        </div>
      </div>
    </div>
  );
  return it.link ? <Link to={it.link}>{inner}</Link> : inner;
}
